import Logo from "./Logo";
import {
	getSidebarBottomLinksByRole,
	getSidebarSectionsByRole,
} from "../../lib/constants/navigation";
import { Link, useLocation } from "react-router-dom";
import classNames from "classnames";
import { ThemeMode } from "../../hooks/useThemeMode";
import { HiChevronDown, HiChevronLeft, HiChevronRight, HiChevronUp, HiOutlineQuestionMarkCircle } from "react-icons/hi";
import { AdminRole } from "../../types/constants.types";
import { OWNER_TOUR_START_EVENT } from "../OwnerTour/tourSteps";
import { useSelector } from "react-redux";
import { RootState } from "../../state/store";
import { useState } from "react";

const linkClasses = `flex items-center gap-2 font-light 
                    px-3 py-2.5 hover:bg-white/50 dark:hover:bg-white/10 hover:no-underline active:bg-primary-50
                    rounded-xl transition-colors`;
const linkDissabledClasses = `flex items-center gap-2 font-light text-gray-400
                    px-3 py-2.5 hover:bg-gray-50 hover:no-underline rounded-xl`;
const sectionLabelClasses = "px-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-gray-500/80 dark:text-gray-400/80";

type SidebarProps = {
	mode: ThemeMode;
	collapsed: boolean;
	onToggleCollapse: () => void;
	hideCollapseToggle?: boolean;
};

export default function Sidebar({ mode, collapsed, onToggleCollapse, hideCollapseToggle = false }: SidebarProps) {
	const { pathname } = useLocation();
	const currentUserRole = useSelector(
		(state: RootState) => state.auth.role,
	);
	const currentUserEmail = useSelector(
		(state: RootState) => state.auth.email,
	);
	const roleBasedSections = getSidebarSectionsByRole(currentUserRole, currentUserEmail);
	const roleBasedBottomLinks = getSidebarBottomLinksByRole(currentUserRole);
	const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
		Object.fromEntries(roleBasedSections.map((section) => [section.key, true]))
	);

	const toggleSection = (key: string) => {
		setExpandedSections((current) => ({
			...current,
			[key]: !(current[key] ?? true),
		}));
	};

	const renderSidebarItem = (item: any) => {
		const isActive = pathname === item.path;
		const baseClass = item.active ? linkClasses : linkDissabledClasses;
		const activeClass = isActive
			? "text-base bg-white/80 text-primary-100 dark:bg-white/15 dark:text-white shadow-sm"
			: "";
		const collapsedClass = collapsed ? "justify-center px-2 gap-0" : "";
		const content = (
			<>
				<span className="text-xl flex-shrink-0">{item.icon}</span>
				{!collapsed && <span className="truncate">{item.label}</span>}
			</>
		);

		if (!item.active) {
			return (
				<span
					className={classNames(activeClass, baseClass, collapsedClass)}
					title={collapsed ? item.label : undefined}
				>
					{content}
				</span>
			);
		}

		if (item.isExternal || /^https?:\/\//i.test(item.path)) {
			return (
				<a
					className={classNames(activeClass, baseClass, collapsedClass)}
					href={item.path}
					target="_blank"
					rel="noopener noreferrer"
					title={collapsed ? item.label : undefined}
				>
					{content}
				</a>
			);
		}

		return (
			<Link
				className={classNames(activeClass, baseClass, collapsedClass)}
				to={item.path}
				title={collapsed ? item.label : undefined}
			>
				{content}
			</Link>
		);
	};

	return (
		<div className={classNames(
			"flex flex-col text-gray-900 dark:text-gray-100 h-full min-h-0 transition-all duration-300 ease-in-out overflow-hidden",
			collapsed ? "w-[52px] p-2" : "w-64 p-3"
		)}>
			{/* Logo + collapse toggle */}
			<div className={classNames(
				"flex items-center py-3",
				collapsed ? "justify-center px-0" : "justify-between px-2"
			)}>
				{collapsed ? (
					<Logo mode={mode} variant="icon" className="h-8 w-auto max-w-8" />
				) : (
					<Logo mode={mode} variant="main" />
				)}
				{!hideCollapseToggle && (
					<button
						onClick={onToggleCollapse}
						className={classNames(
							"p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-white/60 dark:hover:bg-white/10 transition-colors flex-shrink-0",
							collapsed ? "hidden" : "ml-2"
						)}
						aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
					>
						<HiChevronLeft fontSize={18} />
					</button>
				)}
			</div>

			{/* Expand button when collapsed */}
			{collapsed && !hideCollapseToggle && (
				<button
					onClick={onToggleCollapse}
					className="mx-auto mt-1 p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-white/60 dark:hover:bg-white/10 transition-colors"
					aria-label="Expand sidebar"
				>
					<HiChevronRight fontSize={18} />
				</button>
			)}

			{/* Main nav links */}
			<div className="flex-1 min-h-0 py-4 pr-1 overflow-y-auto">
				<div className="flex flex-col gap-4">
					{roleBasedSections.map((section) => {
						const isExpanded = expandedSections[section.key] ?? true;
						return (
							<section key={section.key} className="flex flex-col gap-2">
								{!collapsed && (
									<button
										type="button"
										onClick={() => toggleSection(section.key)}
										className="flex items-center justify-between px-3 py-1 text-left hover:bg-white/20 dark:hover:bg-white/5 rounded-lg transition-colors"
									>
										<span className={sectionLabelClasses}>{section.label}</span>
										<span className="text-gray-500 dark:text-gray-400">
											{isExpanded ? <HiChevronUp size={14} /> : <HiChevronDown size={14} />}
										</span>
									</button>
								)}
								{isExpanded && (
									<div className="flex flex-col gap-1">
										{section.links.map((item) => (
											<div key={item.path}>{renderSidebarItem(item)}</div>
										))}
									</div>
								)}
							</section>
						);
					})}
				</div>
			</div>

			{/* Bottom nav links */}
			<div className={classNames(
				"mt-auto shrink-0 py-4 flex flex-col gap-1 border-t border-white/50 dark:border-white/10"
			)}>
				{currentUserRole === AdminRole.KIOSK_OWNER && (
					<button
						type="button"
						onClick={() => window.dispatchEvent(new CustomEvent(OWNER_TOUR_START_EVENT))}
						className={classNames(linkClasses, "w-full text-left", collapsed ? "justify-center px-2 gap-0" : "")}
						title={collapsed ? "Product tour" : undefined}
						aria-label="Start the product tour"
					>
						<span className="text-xl flex-shrink-0"><HiOutlineQuestionMarkCircle /></span>
						{!collapsed && <span className="truncate">Product tour</span>}
					</button>
				)}
				{roleBasedBottomLinks.map((item) => (
					<div key={item.path}>{renderSidebarItem(item)}</div>
				))}
			</div>
		</div>
	);
}
